'use client';
import { atom, useAtom } from 'jotai';
import { useCallback } from 'react';

interface InterceptionState {
  isIntercepted: boolean;
  message?: string;
  pendingFn?: {
    fn: (...args: any[]) => any;
    args: any[];
  };
}

const interactionAtom = atom<InterceptionState>({
  isIntercepted: false,
});

export const useInteractionGuard = () => {
  const [state, setState] = useAtom(interactionAtom);

  const interceptInteraction = useCallback(
    <T extends (...args: any[]) => any>(fn: T, message?: string, navigateThrough?: boolean) => {
      return (...args: Parameters<T>): ReturnType<T> | void => {
        // Handle event if first argument is an event
        if (args[0]?.preventDefault && args[0]?.stopPropagation) {
          args[0].preventDefault();
          args[0].stopPropagation();
        }

        // If navigateThrough is true, execute the function directly without interception
        if (navigateThrough) {
          return fn(...args);
        }

        setState({
          isIntercepted: true,
          message,
          pendingFn: { fn, args },
        });
      };
    },
    [],
  );

  return {
    isIntercepted: state.isIntercepted,
    message: state.message,
    pendingFn: state.pendingFn,
    interceptInteraction,
    clearInterception: () =>
      setState({ isIntercepted: false, message: undefined, pendingFn: undefined }),
  };
};
